import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ResponseStatus } from "../../actions/types";
import recipientService from "../../services/recipient.service";
import {
  handleGetAllRecipients,
  handleGetMyRecipients,
  onChangeRecipientInState,
  onPaginationChange,
} from "../reducers/recipients.reducer";
import { Recipient } from "../../pages/shared/models/user/recipient/recipient";

export const getMyEveryRecipient = createAsyncThunk(
  "getAllRecipients",
  async (s, thunkApi) => {
    try {
      const recipients = await recipientService.getMyRecipients();
      return { allRecipients: recipients };
    } catch (error) {
      return {
        allRecipients: [],
        errors: thunkApi.rejectWithValue(error),
      };
    }
  },
);

export const getMyRecipients = createAsyncThunk(
  "getMyRecipients",
  async (props: { forceLoad?: boolean; page?: number }, thunkApi) => {
    try {
      const limit = (thunkApi.getState() as any).recipientDataReducer
        .myRecipientsData.pageSize;
      const recipients = await recipientService.getMyRecipients(
        limit,
        props.page,
      );

      return { myRecipients: recipients };
    } catch (error) {
      return {
        access_token: null,
        errors: thunkApi.rejectWithValue(error),
      };
    }
  },
  {
    condition(
      props: { forceLoad?: boolean; page?: number },
      { getState },
    ): boolean {
      const allSliceStates = getState() as { [key: string]: any };
      const recipientSlice = allSliceStates["recipientDataReducer"];

      return (
        props.forceLoad ||
        !(
          recipientSlice.myRecipientsData.recipients &&
          recipientSlice.myRecipientsData.recipients.length
        )
      );
    },
  },
);

export const recipientsSlice = createSlice({
  name: "recipients",
  initialState: {
    allRecipients: [],
    myRecipientsData: {
      recipients: [] as Recipient[],
      currentPage: 0,
      pageSize: 12,
      totalItems: 0,
    },
    errors: [],
    isLoading: false,
  } as RecipientsSliceData,
  reducers: {
    changeRecipientInState: onChangeRecipientInState,
    changePagination: onPaginationChange,
  },
  extraReducers: (builder) => {
    builder.addCase(getMyEveryRecipient.pending, (state) =>
      handleGetAllRecipients(state, ResponseStatus.PENDING, null),
    );
    builder.addCase(getMyEveryRecipient.fulfilled, (state, action) =>
      handleGetAllRecipients(state, ResponseStatus.SUCCESS, action.payload),
    );
    builder.addCase(getMyEveryRecipient.rejected, (state, action) =>
      handleGetAllRecipients(state, ResponseStatus.FAIL, action.payload),
    );
    builder.addCase(getMyRecipients.pending, (state) =>
      handleGetMyRecipients(state, ResponseStatus.PENDING, null),
    );
    builder.addCase(getMyRecipients.fulfilled, (state, action) =>
      handleGetMyRecipients(state, ResponseStatus.SUCCESS, action.payload),
    );
    builder.addCase(getMyRecipients.rejected, (state, action) =>
      handleGetMyRecipients(state, ResponseStatus.FAIL, action.payload),
    );
  },
});

export interface RecipientsSliceData {
  allRecipients: Recipient[];
  myRecipientsData: {
    recipients: Recipient[];
    currentPage: number;
    pageSize: number;
    totalItems: number;
  };
  errors: any[];
  isLoading: boolean;
}

export default recipientsSlice.reducer;
export const { changePagination, changeRecipientInState } =
  recipientsSlice.actions;
